import React from "react";
import { cn } from "../../lib/utils";
import {
  TestRunQualitativeEvaluationSummary,
  CriterionBreakdown,
  ScoreCount,
} from "../../model_gen/evaluation";
import { isDeterministicType } from "../../utils/evaluationTypes";

const TAG_COLORS = [
  "#22c55e",
  "#ef4444",
  "#f97316",
  "#3b82f6",
  "#a855f7",
  "#eab308",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#a3a3a3",
];

function getEffectiveScoringType(bd: CriterionBreakdown): string {
  if (bd.scoring_type) return bd.scoring_type;
  if (isDeterministicType(bd.criteria_type)) return "boolean";
  return "numeric";
}

function consolidateScores(scores: ScoreCount[]): ScoreCount[] {
  const map = new Map<string, ScoreCount>();
  for (const sc of scores) {
    const key = `${sc.score}:${sc.label ?? ""}`;
    const existing = map.get(key);
    if (existing) {
      existing.count += sc.count;
    } else {
      map.set(key, { ...sc });
    }
  }
  return Array.from(map.values());
}

interface Props {
  testRunResults: TestRunQualitativeEvaluationSummary | null;
  selectedCriteria: string | null;
  selectedScores: number[];
  onCriteriaSelect: (criteria: string | null) => void;
  onScoreClick: (score: number) => void;
  onScoreFilterClear: () => void;
}

const TestRunScoreFilter: React.FC<Props> = ({
  testRunResults,
  selectedCriteria,
  selectedScores,
  onCriteriaSelect,
  onScoreClick,
  onScoreFilterClear,
}) => {
  const breakdowns = testRunResults?.criteria_breakdowns || [];
  const overallScores = testRunResults?.request_scores || [];
  if (breakdowns.length === 0 && overallScores.length === 0) return null;

  const activeBd: CriterionBreakdown | null =
    breakdowns.find((b) => b.criteria_name === selectedCriteria) || null;
  const scoringType = activeBd ? getEffectiveScoringType(activeBd) : "numeric";
  const rawScores = activeBd?.score_distribution || overallScores;
  const activeScores = consolidateScores(rawScores);
  const totalScored = activeScores.reduce((s, i) => s + i.count, 0);

  const renderScoreButtons = () => {
    if (scoringType === "boolean") {
      const passEntry = activeScores.find(
        (s) => s.label === "PASS" || s.label === "Pass" || s.score === 1
      );
      const failEntry = activeScores.find(
        (s) => s.label === "FAIL" || s.label === "Fail" || s.score === 0
      );
      const entries: { score: number; label: string; color: string; count: number }[] = [
        { score: 1, label: "Pass", color: "#22c55e", count: passEntry?.count || 0 },
        { score: 0, label: "Fail", color: "#ef4444", count: failEntry?.count || 0 },
      ];
      return (
        <>
          <div className="flex rounded-md overflow-hidden h-6">
            {entries.map(({ score, label, color, count }) => {
              if (count === 0) return null;
              const pct = (count / totalScored) * 100;
              const isActive = selectedScores.length === 0 || selectedScores.includes(score);
              return (
                <div
                  key={score}
                  onClick={() => onScoreClick(score)}
                  title={`${label}: ${count.toLocaleString()} (${pct.toFixed(1)}%)`}
                  className="h-full transition-all duration-300 cursor-pointer"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: color,
                    opacity: isActive ? 1 : 0.3,
                    boxShadow: selectedScores.includes(score)
                      ? "inset 0 0 0 2px rgba(255,255,255,0.6)"
                      : "none",
                  }}
                />
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {entries.map(({ score, label, color, count }) => {
              const pct = totalScored > 0 ? (count / totalScored) * 100 : 0;
              const isActive = selectedScores.length === 0 || selectedScores.includes(score);
              return (
                <button
                  key={score}
                  onClick={() => onScoreClick(score)}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-left transition-all",
                    selectedScores.includes(score)
                      ? "border-foreground/30 bg-muted/60"
                      : "border-border hover:bg-muted/40",
                    !isActive && "opacity-40"
                  )}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="min-w-0">
                    <div className="text-xs font-gc-med text-foreground truncate">{label}</div>
                    <div className="text-[10px] text-muted-foreground font-gc-reg">
                      {count} · {pct.toFixed(1)}%
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      );
    }

    if (scoringType === "tags") {
      return (
        <>
          <div className="flex rounded-md overflow-hidden h-6">
            {activeScores.map((sc, idx) => {
              const count = sc.count;
              if (count === 0) return null;
              const pct = (count / totalScored) * 100;
              const color = TAG_COLORS[idx % TAG_COLORS.length];
              const isActive = selectedScores.length === 0 || selectedScores.includes(sc.score);
              return (
                <div
                  key={sc.score}
                  onClick={() => onScoreClick(sc.score)}
                  title={`${sc.label || sc.score}: ${count.toLocaleString()} (${pct.toFixed(1)}%)`}
                  className="h-full transition-all duration-300 cursor-pointer"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: color,
                    opacity: isActive ? 1 : 0.3,
                    boxShadow: selectedScores.includes(sc.score)
                      ? "inset 0 0 0 2px rgba(255,255,255,0.6)"
                      : "none",
                  }}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            {activeScores.map((sc, idx) => {
              const pct = totalScored > 0 ? (sc.count / totalScored) * 100 : 0;
              const color = TAG_COLORS[idx % TAG_COLORS.length];
              const isActive = selectedScores.length === 0 || selectedScores.includes(sc.score);
              return (
                <button
                  key={sc.score}
                  onClick={() => onScoreClick(sc.score)}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-left transition-all",
                    selectedScores.includes(sc.score)
                      ? "border-foreground/30 bg-muted/60"
                      : "border-border hover:bg-muted/40",
                    !isActive && "opacity-40"
                  )}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="min-w-0">
                    <div className="text-xs font-gc-med text-foreground truncate">{sc.label || sc.score}</div>
                    <div className="text-[10px] text-muted-foreground font-gc-reg">
                      {sc.count} · {pct.toFixed(1)}%
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      );
    }

    const sorted = [...activeScores].sort((a, b) => a.score - b.score);
    const cfg = activeBd?.scoring_config as Record<string, any> | undefined;
    const levels: { value: number; label: string }[] = cfg?.levels || [];
    const minVal = cfg?.min ?? 1;

    return (
      <>
        <div className="flex rounded-md overflow-hidden h-6">
          {sorted.map((sc) => {
            if (sc.count === 0) return null;
            const pct = (sc.count / totalScored) * 100;
            const lvl = levels.find((l) => l.value === sc.score);
            const label = lvl?.label || sc.label || String(sc.score);
            const colorIdx = levels.length > 0
              ? levels.findIndex((l) => l.value === sc.score)
              : sc.score - minVal;
            const color = TAG_COLORS[Math.max(0, colorIdx) % TAG_COLORS.length];
            const isActive = selectedScores.length === 0 || selectedScores.includes(sc.score);
            return (
              <div
                key={sc.score}
                onClick={() => onScoreClick(sc.score)}
                title={`${label}: ${sc.count.toLocaleString()} (${pct.toFixed(1)}%)`}
                className="h-full transition-all duration-300 cursor-pointer"
                style={{
                  width: `${pct}%`,
                  backgroundColor: color,
                  opacity: isActive ? 1 : 0.3,
                  boxShadow: selectedScores.includes(sc.score)
                    ? "inset 0 0 0 2px rgba(255,255,255,0.6)"
                    : "none",
                }}
              />
            );
          })}
        </div>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(sorted.length, 5)}, 1fr)` }}
        >
          {sorted.map((sc) => {
            const pct = totalScored > 0 ? (sc.count / totalScored) * 100 : 0;
            const lvl = levels.find((l) => l.value === sc.score);
            const label = lvl?.label || sc.label || String(sc.score);
            const colorIdx = levels.length > 0
              ? levels.findIndex((l) => l.value === sc.score)
              : sc.score - minVal;
            const color = TAG_COLORS[Math.max(0, colorIdx) % TAG_COLORS.length];
            const isActive = selectedScores.length === 0 || selectedScores.includes(sc.score);
            return (
              <button
                key={sc.score}
                onClick={() => onScoreClick(sc.score)}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-left transition-all",
                  selectedScores.includes(sc.score)
                    ? "border-foreground/30 bg-muted/60"
                    : "border-border hover:bg-muted/40",
                  !isActive && "opacity-40"
                )}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <div className="min-w-0">
                  <div className="text-xs font-gc-med text-foreground truncate">{label}</div>
                  <div className="text-[10px] text-muted-foreground font-gc-reg">
                    {sc.count} · {pct.toFixed(1)}%
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="mb-6 space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => onCriteriaSelect(null)}
          className={cn(
            "px-3 py-1.5 text-xs rounded-full border transition-colors font-gc-reg",
            !selectedCriteria
              ? "bg-maitai-green/10 text-maitai-green border-maitai-green/30 font-gc-med"
              : "border-border text-muted-foreground hover:text-foreground"
          )}
        >
          All
        </button>
        {breakdowns.map((bd) => (
          <button
            key={bd.criteria_name}
            onClick={() =>
              onCriteriaSelect(
                selectedCriteria === bd.criteria_name ? null : bd.criteria_name
              )
            }
            className={cn(
              "px-3 py-1.5 text-xs rounded-full border transition-colors font-gc-reg",
              selectedCriteria === bd.criteria_name
                ? "bg-maitai-green/10 text-maitai-green border-maitai-green/30 font-gc-med"
                : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {bd.criteria_name}
            {bd.avg_numeric_score != null && (
              <span className="ml-1.5 text-[10px] opacity-70">
                {bd.avg_numeric_score.toFixed(1)}
              </span>
            )}
          </button>
        ))}
      </div>

      {totalScored > 0 && (
        <div className="space-y-3">
          {renderScoreButtons()}

          {selectedScores.length > 0 && (
            <button
              onClick={onScoreFilterClear}
              className="text-xs text-blue-500 hover:text-blue-400 font-gc-med"
            >
              Clear score filter
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TestRunScoreFilter;
